/**
 * Admin JWT storage and session lifecycle events.
 *
 * Tokens live in localStorage under `adm_access` / `adm_refresh` (separate from the client app's
 * keys) and are always read from storage, never cached in memory: another tab may have rotated
 * them (SPEC-FRONTEND §2, rule 3). The HTTP layer reports a dead session through `endSession`;
 * `AuthProvider` subscribes via `onSessionEnd` and updates React state.
 */
import type { TokenPair } from '@/api/types';

export const ACCESS_TOKEN_KEY = 'adm_access';
export const REFRESH_TOKEN_KEY = 'adm_refresh';

/** Why the session ended: explicit sign-out, rejected/expired refresh token, or blocked account. */
export type SessionEndReason = 'logout' | 'expired' | 'blocked';

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, quota): the session simply won't survive a reload.
  }
}

export const tokenStorage = {
  getAccess: (): string | null => read(ACCESS_TOKEN_KEY),
  getRefresh: (): string | null => read(REFRESH_TOKEN_KEY),
  hasSession: (): boolean => read(REFRESH_TOKEN_KEY) !== null,
  set(tokens: TokenPair): void {
    // Access first: a tab reacting to the refresh-key `storage` event must already see it.
    write(ACCESS_TOKEN_KEY, tokens.access);
    write(REFRESH_TOKEN_KEY, tokens.refresh);
  },
  clear(): void {
    write(ACCESS_TOKEN_KEY, null);
    write(REFRESH_TOKEN_KEY, null);
  },
};

type SessionEndListener = (reason: SessionEndReason) => void;

const listeners = new Set<SessionEndListener>();

/** Subscribes to forced session ends (refresh failure, blocked account). Returns an unsubscribe. */
export function onSessionEnd(listener: SessionEndListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Drops the tokens and notifies subscribers once. Several failing requests may race here;
 * only the first one (while tokens still exist) produces an event.
 */
export function endSession(reason: SessionEndReason): void {
  const hadSession = tokenStorage.getRefresh() !== null || tokenStorage.getAccess() !== null;
  tokenStorage.clear();
  if (!hadSession) return;
  listeners.forEach((listener) => listener(reason));
}
